import { sanitizeHTML } from 'src/app/core/helpers/text-sanitizer';
import {
  deconstructConditionalFormula,
  deconstructFormula,
} from 'src/app/project/components/formula-builder/utils/deconstruct-formula';
import {
  EFormulaPieceType,
  TConditionalFormula,
  TFormulaPiece,
} from 'src/app/project/components/formula-builder/utils/formula-input.type';
import { translate } from 'src/app/project/features/translate/translate';
import { getCustomFieldIcon } from '../../site/site-options/utils/get-field-icon';
import { TWorkspaceCustomFields } from '../custom-fields.model';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

export function setFormulaTooltipTitle(
  customFieldId: string,
  customFields: TWorkspaceCustomFields,
): Promise<string> {
  if (customFields[customFieldId].formula.startsWith('if')) {
    return createConditionalFormulaTooltip(
      customFields,
      deconstructConditionalFormula(customFields[customFieldId].formula),
    );
  } else {
    return createFormulaTooltip(
      customFields,
      deconstructFormula(customFields[customFieldId].formula),
    );
  }
}

function createFormulaTooltip(
  customFields: TWorkspaceCustomFields,
  formulaPieces: TFormulaPiece[],
  isConditional: boolean = false,
): Promise<string> {
  return new Promise((resolve) => {
    const formulaHidden = formulaPieces.some(
      (piece) =>
        piece.type === EFormulaPieceType.CUSTOM_FIELD &&
        (!customFields?.[piece.value] || !customFields[piece.value].display),
    );

    if (formulaHidden) {
      resolve(translate('formula_result'));
      return;
    }

    const operatorsList = formulaPieces.map((piece) => getPieceHTML(piece, customFields));

    Promise.all(operatorsList).then((operatorsListResponse) => {
      const tooltip = `<div class="tooltipFormula">
    ${!isConditional ? '<div class="tooltipFormula__letterF">f</div>' : ''}
    ${operatorsListResponse.join('')}
  </div>`;

      resolve(tooltip);
    });
  });
}

function createConditionalFormulaTooltip(
  customFields: TWorkspaceCustomFields,
  formulaPieces: TConditionalFormula,
): Promise<string> {
  const { condition, truthy, falsey } = formulaPieces; // Extract formulas

  return Promise.all([
    createFormulaTooltip(customFields, condition, true),
    createFormulaTooltip(customFields, truthy, true),
    createFormulaTooltip(customFields, falsey, true),
  ]).then(([tooltip1, tooltip2, tooltip3]) => {
    const formulaHidden = [...condition, ...truthy, ...falsey].some(
      (piece) =>
        piece.type === EFormulaPieceType.CUSTOM_FIELD &&
        (!customFields?.[piece.value] || !customFields[piece.value].display),
    );

    if (formulaHidden) {
      return translate('formula_result');
    }

    return `<div class="formulaTooltip-container">
        <span>${translate('conditional_formula_condition_label')}</span>
        ${tooltip1}
        <span>${translate('conditional_formula_truthy_label')}</span>
        ${tooltip2}
        <span>${translate('conditional_formula_falsy_label')}</span>
        ${tooltip3}
      </div>`;
  });
}

function getPieceHTML(piece: TFormulaPiece, customFields: TWorkspaceCustomFields): Promise<string> {
  switch (piece.type) {
    case EFormulaPieceType.OPERATOR:
      return Promise.resolve(`<div class="tooltipFormula__operator">${piece.value}</div>`);
    case EFormulaPieceType.CUSTOM_FIELD: {
      return new Promise((resolve) => {
        const customField = customFields?.[piece.value];
        const fieldIcon = getCustomFieldIcon(piece.value);
        const icon = iconMap[fieldIcon];

        return resolve(`<div class="tooltipFormula__customField">
          ${icon}
            <span>${sanitizeHTML(customField.label)}</span>
         </div>`);
      });
    }
    default:
      return Promise.resolve('');
  }
}
